import { ProfileType } from "../types/profile";
import { WelcompeStepEnum } from "../types/welcome";
import { shouldCollectNir } from "./nir";
import { shouldDeclareFamilyComposition } from "./familyComposition";

/**
 * The ordered "spine" of registration steps a given role actually walks through,
 * used to drive the multi-step progress bar below the header.
 *
 * This mirrors the branching in HomeContent (createUser → handleNextInscription →
 * the stratification→sites→consent effect): the same predicates that decide whether
 * a step is pushed onto the journey stack decide whether it gets a segment here, so
 * the bar shows exactly one segment per screen the user will see.
 *
 * Deliberately excluded from the count:
 *  - ON_BOARDING: an immersive intro (video/swiper/page) that carries its own
 *    pagination (see SwiperOnboarding's dots) and runs with welcomePage=false.
 *  - REGISTRED_SUCCESSFULLY / INVITATION: terminal screens, not a step to complete.
 *  - NOT_INCLUDED: an ineligibility off-ramp, not part of the linear path.
 */
export const getInscriptionSteps = (
  role?: ProfileType,
  allRoles?: ProfileType[]
): WelcompeStepEnum[] => {
  if (!role) return [];

  const steps: WelcompeStepEnum[] = [];
  const attributes = role.attributes;

  // Inclusion questionnaire, when the role defines one (HomeContent.tsx: createUser).
  if (role.inclusion_criteria?.has_inclusion_questions) {
    steps.push(WelcompeStepEnum.INCLUSION);
  }

  // The identity/credentials form always renders in a signup (auto-submitted for
  // no-sign-in roles, but still the mandatory INSCRIPTION step).
  steps.push(WelcompeStepEnum.INSCRIPTION);

  // Family composition ("how many <data-only role>?") right after the inscription step —
  // the numéro de dossier entered there drives the skip pre-check. Same predicate as
  // handleNextInscription. (When the family is already declared, the step auto-completes;
  // the bar simply advances past its segment.)
  if (shouldDeclareFamilyComposition(role, allRoles)) {
    steps.push(WelcompeStepEnum.FAMILY_COMPOSITION);
  }

  // Dedicated NIR screen (SNDS appariement) right after the identity/OTP step —
  // the SAME predicate as handleNextInscription (HomeContent.tsx), via the shared
  // helper, so the bar can never desynchronize from the real navigation.
  if (shouldCollectNir(role)) {
    steps.push(WelcompeStepEnum.NIR);
  }

  // Stratification screen — gated by ANY of the three profiling flags, matching
  // proceedAfterIdentity/handlePreStratification (HomeContent.tsx).
  if (
    attributes?.has_stratification ||
    attributes?.has_profile_attributes ||
    attributes?.has_birthdate
  ) {
    steps.push(WelcompeStepEnum.STRATIFICATION);
  }

  // Site selection is collected BEFORE consent (so consent-time sequence generation
  // is site-scoped), so it precedes CONSENT in the ordered list too.
  if (attributes?.is_site_requested) {
    steps.push(WelcompeStepEnum.SITES);
  }

  // Consent screen, when the role requires signed/checkbox consent.
  if (role.consent?.has_consent) {
    steps.push(WelcompeStepEnum.CONSENT);
  }

  return steps;
};
